import {
    getSignedUrl,
    uploadFile,
    saveDocMetaDataInDocService,
} from '../../../modules/Company/UploadJourney/components/UploadCard/utils/api/helpers';
import type { ManualUploadFile } from '../types';

export interface ComposeUploadConfig {
    assesseeOrgId: string;
    mnemosyneOrgId: string;
    categoryId: number;
    flowId: number;
}

/**
 * Upload a generated PDF blob (Engagement Letter / One Pager / Bureau-KYC,
 * rendered client-side from its Plate doc) through the same Mnemosyne pipeline
 * a manual UploadCard upload uses — getSignedUrl → S3 PUT → saveDocMetaData —
 * and return the `{ docName, id }` the compose flow tracks in filesMetaData.
 *
 * The returned docId rides the existing `docIds` attachment path, so the email
 * backend attaches it with zero backend changes (the backend has no support for
 * attachEngagementLetter / attachOnePager flags — see RECUR retros).
 */
export async function uploadPdfBlobAsAttachment(blob: Blob, fileName: string, cfg: ComposeUploadConfig): Promise<ManualUploadFile> {
    const safeName = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
    const file = new File([blob], safeName, { type: 'application/pdf' });

    const signResponse = await getSignedUrl(
        { file },
        { assesseeOrgId: cfg.assesseeOrgId, categoryId: cfg.categoryId, mnemosyneOrgId: cfg.mnemosyneOrgId },
    );
    if (!signResponse?.signedUrl) {
        throw new Error('Failed to obtain signed URL for attachment upload');
    }

    const uploadResult = await uploadFile(file, signResponse.signedUrl);
    if (!uploadResult?.url) {
        throw new Error('Failed to upload attachment to S3');
    }

    const docData = await saveDocMetaDataInDocService({
        input: {
            categoryId: cfg.categoryId,
            mnemosyneOrgId: cfg.mnemosyneOrgId,
            fileName: safeName,
            fileSizeKb: file.size / 1_000,
            flowId: cfg.flowId,
            fileType: file.type,
        },
        orgId: cfg.assesseeOrgId,
    });

    if (!docData?.id) {
        throw new Error('Failed to register attachment in document service');
    }

    return { docName: safeName, id: docData.id };
}
